import ExpandableBox from 'components/shared/ExpandableBox'
import { useEffect, useState } from 'react'
import Input from 'components/shared/Input'
import { FormattedMessage, useIntl } from 'react-intl'
import Button from 'components/shared/Button'
import Box from 'components/shared/Box'
import Link from 'components/shared/Link'
import VoucherValid from './VoucherValid'
import {
    GiftCardDto,
    useDeleteCartGiftCardMutation,
    usePatchCartGiftCardMutation,
} from 'store/api-slice/hooks'
import { useApp } from 'store/steps'
import { useCart } from 'store/cart-slice/hooks'
import { catchErrorByStatus, handleError } from 'helpers/errors'
import { useResolutions } from 'store/resolutions'
import { PatchCartGiftCardApiArg } from 'store/api-slice/hooks'
import { AppMode } from 'helpers/types'
import { useSelfcareCart } from 'store/selfcare-cart-slice/hooks'
import Loader from 'components/shared/Loader'
import { pushDataLayer } from 'helpers/analytics'

type CartType = PatchCartGiftCardApiArg['cartType']

interface VoucherCodeProps {
    voucher?: GiftCardDto
}

const VoucherCode = ({ voucher }: VoucherCodeProps) => {
    const { language, appMode } = useApp()
    const { isMobile, isTablet } = useResolutions()
    const { refetchCart, cartCreatorType, isCartFetching } = useCart()
    const { refetchSelfcareCart, isSelfcareCartFetching } = useSelfcareCart()
    const [voucherCode, setVoucherCode] = useState('')
    const [codeError, setCodeError] = useState('')
    const [isVoucherValid, setIsVoucherValid] = useState<boolean | null>(null)
    const [isExpandableBoxOpen, setIsExpandableBoxOpen] = useState(false)
    const { formatMessage } = useIntl()
    const [postGiftCard, postGiftCardStatus] = usePatchCartGiftCardMutation({})
    const [deleteGiftCard, deleteGiftCardStatus] =
        useDeleteCartGiftCardMutation()
    const isLoading =
        postGiftCardStatus?.isLoading ||
        deleteGiftCardStatus?.isLoading ||
        isSelfcareCartFetching ||
        isCartFetching

    useEffect(() => {
        if (voucher && voucher.number) {
            setVoucherCode(voucher.number)
            setIsVoucherValid(true)
        }
    }, [voucher])

    const resetToInitialState = () => {
        setIsVoucherValid(null)
        setIsExpandableBoxOpen(false)
        setVoucherCode('')
    }

    const isCartType = (value: AppMode): CartType | undefined => {
        const creatorTypes: Partial<AppMode>[] = [
            'creator',
            'creator-accommodation',
            'unlimited-card',
            'unlimited-renew',
            'vouchers',
        ]
        if (value) {
            if (creatorTypes.includes(value)) {
                return 'creator'
            }
            if (value === 'selfcare') {
                return 'selfcare'
            }
        }
    }

    const getErrorMessage = (err: unknown) => {
        const { messages, message } = err as MessageErrorModel
        setCodeError(message || messages[0].message)
    }

    const handleChangeVoucherCode = (code: string) => {
        setVoucherCode(code)
        setIsVoucherValid(null)
    }

    const handleAddVoucher = async () => {
        if (!voucherCode.length) {
            setIsVoucherValid(false)
            return
        }

        const isAppMode = isCartType(appMode)
        if (isAppMode) {
            try {
                const resp = await postGiftCard({
                    _locale: language,
                    cartCreatorType,
                    cartType: isAppMode,
                    addGiftCardCommand: {
                        giftCardNumber: voucherCode,
                    },
                })
                if ('data' in resp) {
                    setIsVoucherValid(true)
                    appMode === 'selfcare'
                        ? refetchSelfcareCart()
                        : refetchCart()

                    // < analytics
                    pushDataLayer({
                        event: 'voucher_code',
                        voucher_code: voucherCode,
                        voucher_correctness: 'active',
                    })
                    // analytics >

                    return
                }

                // < analytics
                pushDataLayer({
                    event: 'voucher_code',
                    voucher_code: voucherCode,
                    voucher_correctness: 'not_active',
                })
                // analytics >

                setIsVoucherValid(false)
                handleError(resp)
            } catch (error) {
                if (
                    !catchErrorByStatus({
                        error: error as MessageErrorModel,
                    })
                )
                    getErrorMessage(error)
            }
        }
    }

    const handleRemoveVoucher = async () => {
        const isAppMode = isCartType(appMode)
        if (isAppMode) {
            try {
                const resp = await deleteGiftCard({
                    _locale: language,
                    cartCreatorType,
                    cartType: isAppMode,
                })

                if ('data' in resp) {
                    resetToInitialState()
                    appMode === 'selfcare'
                        ? refetchSelfcareCart()
                        : refetchCart()
                    return
                }

                handleError(resp)
            } catch (error) {
                if (
                    !catchErrorByStatus({
                        error: error as MessageErrorModel,
                    })
                )
                    getErrorMessage(error)
            }
        }
    }

    return (
        <Box mt={[8, 0]} isFullWidth>
            {!isVoucherValid ? (
                <ExpandableBox
                    title={formatMessage({
                        defaultMessage: 'Mam voucher kwotowy',
                        id: 'vouchers.discount.section_trigger_label',
                    })}
                    color="secondary_500"
                    maxHeight={350}
                    isBoxOpen={isExpandableBoxOpen}
                    setIsBoxOpen={setIsExpandableBoxOpen}
                    testIdPrefix="payment-trigger-voucher-code"
                    marginTop={16}
                >
                    <Box
                        direction={['column', 'row']}
                        gap={[16, 24]}
                        isFullWidth
                    >
                        <Input
                            id="voucherCode"
                            type="text"
                            onChange={handleChangeVoucherCode}
                            placeholder={formatMessage({
                                defaultMessage: 'Numer vouchera',
                                id: 'vouchers.discount.code_placeholder',
                            })}
                            maxWidth={510}
                            hasNoMaxWidth
                            isFullWidth={isMobile || isTablet}
                            inputLabelFullWidth
                            isError={isVoucherValid === false}
                            errorMessage={
                                codeError ||
                                formatMessage({
                                    id: 'vouchers.discount.code_error',
                                    defaultMessage:
                                        'Podany voucher jest niepoprawny.',
                                })
                            }
                            value={voucherCode}
                            testId="payment-field-voucher-code"
                            isFocusable={isExpandableBoxOpen}
                        />
                        <Button
                            variant="borderPrimary"
                            width={['100%', '180px']}
                            hasArrow={false}
                            onClick={
                                isVoucherValid
                                    ? () => handleRemoveVoucher()
                                    : () => handleAddVoucher()
                            }
                            isLoading={postGiftCardStatus?.isLoading}
                            hasNoWrap
                            testId={`payment-button-voucher-code-${
                                isVoucherValid ? 'delete' : 'add'
                            }`}
                            isFocusable={isExpandableBoxOpen}
                        >
                            {isVoucherValid ? (
                                <FormattedMessage
                                    id="vouchers.discount.btn_remove_caption"
                                    defaultMessage="Usuń"
                                />
                            ) : (
                                <FormattedMessage
                                    id="global.btn.apply"
                                    defaultMessage="Zastosuj"
                                />
                            )}
                        </Button>
                    </Box>
                </ExpandableBox>
            ) : (
                <Box
                    direction="row"
                    justify="space-between"
                    isFullWidth
                    align="flex-start"
                    gap={16}
                >
                    <VoucherValid
                        voucherCode={voucherCode}
                        isLoading={isLoading}
                    />

                    {isLoading ? (
                        <Loader />
                    ) : (
                        <Link
                            variant="secondary"
                            icon={null}
                            onClick={handleRemoveVoucher}
                        >
                            <FormattedMessage
                                id="vouchers.discount.btn_remove_caption"
                                defaultMessage="Usuń"
                            />
                        </Link>
                    )}
                </Box>
            )}
        </Box>
    )
}

export default VoucherCode
